<script setup>
import {
  buildAvailChartsLockDataPage,
  hasRegFlowDeepLinkPending,
  isAvailChartsLockHash,
  isRegFlowHash,
  LOCATION_AV_CHART_MIN_VIEWPORT_WIDTH,
  prepareRegFlowHashForPulseInit,
  REG_FLOW_CHART_ROUTE_META_NAME,
  resolveLocationAvChartDeepLink,
  resolveRegFlowBootstrapDataPage,
  scrollToLocationAvChart,
} from '~/utils/location-av-chart-deeplink'

const route = useRoute()
const {slugify, locationSharedContent: _lsc, locationLeadGen: _llg, locationShortForm: _lsf, locationDockedNavigation: _ldn, locationFetch, transformFetch, trustPilotReviews, transformTrustPilot} = useLocationData()
const { leadGenData: _leadGenData } = useLeadGenData();
const {setPageHead} = useGlobalFeaturesData();

// Getting the location slug and the state slug to verify the authenticity  of the page
const state = route.params.state;
const location = route.params.locationData;
const error = route.params.locationError;
const _startTime = new Date().getTime();
if (error) {
  throw createError({ statusCode: 404, statusMessage: "Page not found!" })
}

const preview = route.query.preview
if (!preview && location.value.entity.publishedAt == null) {
  throw createError({ statusCode: 404, statusMessage: "Page not found!" })
}

// Cheking state or country in case of intl location
if (!state.includes(slugify(location.value.entity.pulseLocation.address.state)) || (state === 'international-summer-camps' && location.value.entity.pulseLocation.address.country === 'United States')) {
  throw createError({ statusCode: 404, statusMessage: "Page not found!" })
}

// Cheking state or country in case of intl location
if (!state.includes(slugify(location.value.entity.pulseLocation.address.state)) || (state === 'international-summer-camps' && location.value.entity.pulseLocation.address.country === 'United States')) {
  throw createError({ statusCode: 404, statusMessage: "Page not found!" })
}

function cleanTags(tags) {
  const newTags = []
  for (let i = 0; i < tags.length; i++) {
    newTags.push(tags[i].tag)
  }
  return newTags
}

const { data: response, pending: _pending } =  await useAsyncData('locationRequest', async () => {
  try {
    const [sharedContent, shortForm, locationForm, leadGen, dockedNav] = await Promise.all([
      locationFetch('locationSharedContent'),
      locationFetch('locationShortForm'),// locationShortForm(),
      locationFetch('locationLeadGen'),// locationLeadGen(),
      locationFetch('leadGenData'),// leadGenData(),
      locationFetch('locationDockedNavigation'), // locationDockedNavigation()      
    ])
    
    return { 
      sharedContent: transformFetch('locationSharedContent', sharedContent), 
      shortForm: transformFetch('locationShortForm', shortForm), 
      locationForm: transformFetch('locationLeadGen', locationForm), 
      leadGen: transformFetch('leadGenData', leadGen), 
      dockedNav: transformFetch('locationDockedNavigation', dockedNav),      
    }
  }
  catch (error) {
    console.error('Error fetching location data', error)
    return { error: error }
  }
  
})

// Getting the Location shared content once we have verified the page is legit
// const {data: sharedContent} = await locationSharedContent()
const sharedContent = response.value.sharedContent;

// Getting the Location shared content once we have verified the page is legit
// const {data: shortForm} = await locationShortForm();
const shortForm = response.value.shortForm;


// Getting the long form global configuration
// const locationForm = await locationLeadGen()
const locationForm = response.value.locationForm;
const leadGenForm = locationForm.leadGenForm

// Getting the long form global configuration
if (!!location.value.entity.locationTagsArray && location.value.entity.locationTagsArray.length > 0){
  leadGenForm.tagsArray = cleanTags(location.value.entity.locationTagsArray)
} else {
  leadGenForm.tagsArray = cleanTags(locationForm.tagsArray)
}

leadGenForm.locationId = location.value.entity.pulseId
leadGenForm.locationName = location.value.entity.pulseLocation.name

// Ribbon form tags array
const brochureRibbonTags = location.value.entity.tagsArray ? location.value.entity.tagsArray : [];

// Getting Lead Gen fields
// const { data: leadGen } = await leadGenData();
const leadGen = response.value.leadGen;

// Getting Lead Gen fields
// const { data: dockedNav } = await locationDockedNavigation();
const dockedNav = response.value.dockedNav;

const {data: trustPilot} = await trustPilotReviews();
const tpReviews = transformTrustPilot(trustPilot, 'All');

// Knowing when Get more info and Sign up buttons are clicked

const _handleGetMoreInfo = () => {
  // Add logic here for 'Get more info' button
}

const { urlRegisterFunction: _urlRegisterFunction } = useUtils();

const locationEntity = computed(() => location.value.entity)
const hasEmbeddedAvChart = computed(
  () => locationEntity.value?.pulseLocation?.status?.typeId === 1,
)
const avChartDeepLink = computed(() => {
  const pulseLocation = locationEntity.value?.pulseLocation
  // Chart is desktop-only; assume desktop on SSR so `data-page` does not flip after hydration.
  const viewportWidth = import.meta.client
    ? window.innerWidth
    : LOCATION_AV_CHART_MIN_VIEWPORT_WIDTH
  // Bootstrap/Pulse update `window.location.hash` before Vue Router syncs; prefer live hash on client.
  const hash = import.meta.client
    ? (window.location.hash || route.hash)
    : route.hash

  return resolveLocationAvChartDeepLink({
    hash,
    queryCourseId: route.query.course_id,
    queryInterest: route.query.interest,
    locationPulseId: locationEntity.value?.pulseId,
    locationCourses: pulseLocation?.courses,
    locationGenres: pulseLocation?.genres,
    hasEmbeddedChart: hasEmbeddedAvChart.value,
    viewportWidth,
  })
})

const regFlowBootstrapDataPage = computed(() => {
  const pulseLocation = locationEntity.value?.pulseLocation
  return resolveRegFlowBootstrapDataPage({
    queryCourseId: route.query.course_id,
    queryInterest: route.query.interest,
    locationPulseId: locationEntity.value?.pulseId,
    locationCourses: pulseLocation?.courses,
    locationGenres: pulseLocation?.genres,
  })
})

/** Pulse reads `data-page`; use query-driven bootstrap route for deep links (not live hash). */
const locationChartDataPage = computed(() => {
  const lid = locationEntity.value?.pulseId
  const baseDataPage =
    lid != null
      ? buildAvailChartsLockDataPage(lid)
      : '/reg-flow/avail-charts-lock?rgnad=true'

  if (!hasEmbeddedAvChart.value) return baseDataPage

  const hash = import.meta.client
    ? (window.location.hash || route.hash)
    : route.hash
  const hasMarketingParams = Boolean(route.query.course_id || route.query.interest)
  const inDeepLinkContext =
    isRegFlowHash(hash)
    || isAvailChartsLockHash(hash)
    || hasMarketingParams
    || (import.meta.client && hasRegFlowDeepLinkPending())

  if (inDeepLinkContext) {
    return regFlowBootstrapDataPage.value
  }

  return baseDataPage
})

if (hasEmbeddedAvChart.value && locationEntity.value?.pulseId) {
  useHead({
    meta: [
      {
        name: REG_FLOW_CHART_ROUTE_META_NAME,
        content: regFlowBootstrapDataPage.value,
      },
    ],
    script: [
      {
        key: 'location-reg-flow-bootstrap',
        src: '/location-reg-flow-bootstrap.js',
        tagPriority: 0,
      },
    ],
  })
}

const handleSignUp = () => {
  scrollToLocationAvChart({ showPromoBanner: route.params.showPromoBanner })
}

onBeforeMount(() => {
  const hash = route.hash || window.location.hash
  if (!isRegFlowHash(hash)) return
  prepareRegFlowHashForPulseInit({
    pathname: route.path,
    hash,
    pulseChartRoute: regFlowBootstrapDataPage.value,
  })
})

onMounted(() => {
  if (!avChartDeepLink.value.shouldScrollToChart) return

  nextTick(() => {
    scrollToLocationAvChart({ showPromoBanner: route.params.showPromoBanner })
  })
})
// Set meta tags in head for SEO
if(location.value && location.value.entity && location.value.entity.seo) {
  setPageHead(location.value.entity.seo, route.path);
}

</script>

<template>
  <UIModal modalId="get-more-info" modalClass="lead-gen-modal modal-dialog-scrollable" modalTitle="">
    <LayoutLongForm :leadGenForm="leadGenForm" :leadTypeID="'67009'" :isModal="true"/> <!-- Lead type ID for location page -->
  </UIModal>
  <LocationHeroSection :sharedContent="sharedContent" :location="location.entity" :leadGen="leadGen" :shortForm="shortForm" :trustPilotReviews="tpReviews" />
  <LocationSubMenu :location="location.entity" @onSignUpClick="handleSignUp" :navLabels="dockedNav" :showPromoBanner="route.params.showPromoBanner"/>
  <div data-bs-spy="scroll" data-bs-target="#location-navbar" data-bs-offset="0" class="scrollspy" tabindex="0" id="location-page-components">
    <LayoutDynamicComponents v-if="location.entity.overview.length > 0" :sections="location.entity.overview" id="overview" :location="location.entity.pulseLocation"/>
    <LocationAVC
      :location="location.entity"
      v-if="location.entity.pulseLocation.status.typeId == 1 "
      :showPromoBanner="route.params.showPromoBanner"
      :data-page="locationChartDataPage"
    />
    <LocationMap :currentLocation="location.entity.id" :nearby="location.nearest" :locations="location.locations" :mapContent="location.entity.mapContent" :sectionTitle="location.entity.mapTitle" />
    <LayoutDynamicComponents v-if="location.entity.campusLife.length > 0" :sections="location.entity.campusLife" :location="location.entity.pulseLocation" id="campusLife"/>
    <LayoutDynamicComponents v-if="location.entity.instructors.length > 0" :sections="location.entity.instructors" id="instructors" :location="location.entity.pulseLocation"/>
    <LayoutDynamicComponents v-if="location.entity.faqs.length > 0" :sections="location.entity.faqs" id="faqs" :location="location.entity.pulseLocation"/>
  </div>  
  <LayoutRibbonForm v-if="location.entity.brochureRibbon" :ribbonTags="brochureRibbonTags" />
  <LayoutBackToTop v-if="location.entity.jumpToTop"/>
  <ChatService v-if="location.entity.showChatService"/>
</template>
